/**
 * ShortcodeLookupPage — driver enters a 4-char short code (or tracking
 * number) to jump to a shipment they're handling. Mirrors Flutter
 * `delivery_lookup_sheet.dart`. QR scanning lands in Phase 7.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Search, AlertCircle } from 'lucide-react';
import { useShortcodeLookup } from '@/hooks/useShipments';
import { AppLayout } from '@/components/layout/AppLayout';
import { TextField } from '@/components/common/TextField';
import { Button } from '@/components/common/Button';

export default function ShortcodeLookupPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const lookup = useShortcodeLookup();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const c = code.trim().toUpperCase();
    if (!c) return;
    try {
      const s = await lookup.mutateAsync(c);
      navigate(`/shipments/${s.id}`);
    } catch (err: any) {
      setError(err?.message || t('shortcode_not_found', 'الرمز غير موجود'));
    }
  }

  return (
    <AppLayout>
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-text-secondary mb-4 hover:text-primary">
        <ArrowRight size={18} /> {t('back', 'رجوع')}
      </button>
      <div className="bg-surface border border-border rounded-2xl p-6 max-w-md mx-auto">
        <h1 className="text-xl font-bold text-primary mb-2 flex items-center gap-2">
          <Search size={20} /> {t('shortcode_lookup', 'بحث برمز مختصر')}
        </h1>
        <p className="text-text-secondary text-sm mb-5">
          {t('shortcode_intro', 'أدخل الرمز المختصر (٤ أحرف) أو رقم التتبّع الكامل')}
        </p>
        <form onSubmit={submit} className="space-y-3">
          <TextField
            dir="ltr"
            placeholder="ABCD أو CW-XXXXXX-XXXX"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            autoFocus
            className="font-mono text-lg tracking-widest text-center"
          />
          {error && (
            <div className="bg-error/10 text-error rounded-xl p-3 text-sm flex items-center gap-2">
              <AlertCircle size={16} /> {error}
            </div>
          )}
          <Button type="submit" loading={lookup.isPending} fullWidth>
            {t('open_shipment', 'افتح الشحنة')}
          </Button>
        </form>
      </div>
    </AppLayout>
  );
}
